import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  RecordVoiceOver as SuggestionIcon,
  Send as SendIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { InventoryItem } from '../../utils/types';
import { ProductSuggestionSettings } from '../../types/storeSettings';
import { useAuth } from '../../contexts/AuthContext';
import { useEffect } from 'react';

interface ProductSuggestionModalProps {
  open: boolean;
  onClose: () => void;
  product: InventoryItem;
  settings: ProductSuggestionSettings;
}

const ProductSuggestionModal: React.FC<ProductSuggestionModalProps> = ({
  open,
  onClose,
  product,
  settings
}) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    suggestion: '',
    link: window.location.href // رابط المنتج الحالي
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // تعبئة البيانات تلقائياً إذا كان المستخدم مسجلاً
  useEffect(() => {
    if (currentUser) {
      setFormData(prev => ({
        ...prev,
        name: currentUser.name || prev.name,
        // ملاحظة: قد لا يتوفر رقم الهاتف أو العنوان مباشرة في كائن User 
        // سنحاول الحصول عليهما إذا كانا متاحين (مثلاً من خصائص إضافية)
        phone: (currentUser as any).phone || prev.phone,
        address: (currentUser as any).address || prev.address
      }));
    }
  }, [currentUser]);

  // إضافة تفاصيل المنتج للاقتراح تلقائياً
  useEffect(() => {
    if (product && !formData.suggestion) {
      const priceText = product.sellingPrice ? ` (السعر الحالي: ${product.sellingPrice})` : '';
      setFormData(prev => ({
        ...prev,
        suggestion: `بخصوص المنتج: ${product.name}${priceText}\n`
      }));
    }
  }, [product]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (settings.destinationType === 'system') {
        // الحفظ في الفايربيز
        await addDoc(collection(db, 'product_suggestions'), {
          ...formData,
          email: currentUser?.email || '',
          productId: product.id,
          productName: product.name,
          status: 'new',
          createdAt: serverTimestamp()
        });
        setSuccess(true);
      } else if (settings.destinationType === 'whatsapp') {
        // الإرسال عبر الواتساب
        const message = `اقتراح لسعر منتج: ${product.name}\nالاسم: ${formData.name}\nالهاتف: ${formData.phone}\nالعنوان: ${formData.address}\nالاقتراح: ${formData.suggestion}\nرابط المنتج: ${formData.link}`;
        const whatsappUrl = `https://wa.me/${settings.destinationValue}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
        onClose();
      } else if (settings.destinationType === 'email') {
        // الإرسال عبر البريد
        const subject = `اقتراح سعر منتج: ${product.name}`;
        const body = `الاسم: ${formData.name}\nالهاتف: ${formData.phone}\nالعنوان: ${formData.address}\nالاقتراح: ${formData.suggestion}\nرابط المنتج: ${formData.link}`;
        const mailtoUrl = `mailto:${settings.destinationValue}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        window.location.href = mailtoUrl;
        onClose();
      }
    } catch (err: any) {
      console.error('Error submitting suggestion:', err);
      setError('حدث خطأ أثناء إرسال الاقتراح. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Dialog open={open} onClose={() => { setSuccess(false); onClose(); }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <SuggestionIcon color="success" sx={{ mr: 1 }} />
          تم الإرسال بنجاح
        </DialogTitle>
        <DialogContent>
          <Typography>شكراً لك على اهتمامك واقتراحك. سيقوم فريقنا بمراجعة طلبك والرد عليك في أقرب وقت ممكن.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setSuccess(false); onClose(); }}>إغلاق</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <SuggestionIcon sx={{ mr: 1, color: 'primary.main' }} />
        {settings.buttonText}
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            الرجاء تزويدنا بالتفاصيل الخاصة بالاقتراح وسنقوم بالتواصل معك.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="الاسم بالكامل"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                label="رقم الهاتف"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                label="العنوان"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                multiline
                rows={4}
                label="الاقتراح أو تفاصيل المنتج بسعر أقل"
                placeholder="اكتب التفاصل هنا، مثل رابط الموقع الآخر أو السعر المقترح..."
                value={formData.suggestion}
                onChange={(e) => setFormData({ ...formData, suggestion: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>إلغاء</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} /> : 
              settings.destinationType === 'whatsapp' ? <WhatsAppIcon /> :
              settings.destinationType === 'email' ? <EmailIcon /> :
              <SendIcon />
            }
          >
            إرسال الاقتراح
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ProductSuggestionModal;

// نحتاج لإضافة استيراد Grid المفقود
import { Grid } from '@mui/material';
